import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress, Tooltip } from '@mui/material';
import { ArrowLeft, ChevronRight, UserMinus, UserPlus, Users } from 'lucide-react';
import api from '../../../../api/client';
import Pagination from '../../component/Pagination';
import NotificationPopup from '../../component/NotificationPopup';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import { apiErrorMessage, formatGroupDate } from './candidateGroupUtils';

const hoverTipSlotProps = {
  popper: {
    modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
  },
  tooltip: {
    sx: {
      background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
      color: '#fff',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.01em',
      borderRadius: '8px',
      px: 1.25,
      py: 0.6,
      boxShadow: '0 8px 20px rgba(14, 165, 233, 0.28)',
      maxWidth: 280,
    },
  },
};

const formatInterviewScore = (score) => (
  score === null || score === undefined ? '—' : `${Number(score).toFixed(2)}%`
);

const scoreClass = (score) => {
  if (score === null || score === undefined) return 'text-gray-500';
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-sky-600';
  if (score >= 40) return 'text-orange-600';
  return 'text-red-600';
};

const inviteStatusClass = {
  Active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Used: 'border-sky-200 bg-sky-50 text-sky-700',
  Expired: 'border-rose-200 bg-rose-50 text-rose-700',
};

export default function CandidateGroupDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_count: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [removeTarget, setRemoveTarget] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadGroup = useCallback(async () => {
    const response = await api.get(`candidate-groups/${id}/`);
    setGroup(response.data);
  }, [id]);

  const loadMembers = useCallback(async () => {
    const response = await api.get(`candidate-groups/${id}/members/`, {
      params: { page, page_size: pageSize },
    });
    const results = response.data?.results || [];
    const count = response.data?.count ?? results.length;
    setMembers(results);
    setPagination({
      current_page: page,
      total_pages: Math.max(1, Math.ceil(count / pageSize)),
      total_count: count,
    });
  }, [id, page, pageSize]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadGroup(), loadMembers()])
      .catch((error) => {
        if (!cancelled) {
          showSnackbar(apiErrorMessage(error, t('candidateGroups.loadFailed', 'Failed to load candidate groups.')), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [loadGroup, loadMembers, t]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ page: '1', page_size: '20' });
        if (candidateSearch.trim()) params.set('search', candidateSearch.trim());
        if (group?.company) params.set('company_id', String(group.company));
        const response = await api.get(`admin/candidates/?${params.toString()}`);
        setCandidateOptions(response.data?.results || []);
      } catch (error) {
        setCandidateOptions([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [pickerOpen, candidateSearch, group?.company]);

  const memberIds = new Set(members.map((member) => member.id));

  const toggleCandidate = (candidateId) => {
    setSelectedIds((current) => (
      current.includes(candidateId)
        ? current.filter((value) => value !== candidateId)
        : [...current, candidateId]
    ));
  };

  const addSelected = async () => {
    if (selectedIds.length === 0) return;
    setAdding(true);
    try {
      const response = await api.post(`candidate-groups/${id}/members/`, { candidate_ids: selectedIds });
      const added = response.data?.added || [];
      const already = response.data?.already_members || [];
      const rejected = response.data?.rejected || [];
      if (added.length) {
        showSnackbar(t('candidateGroups.membersAdded', '{{count}} candidate(s) added.', { count: added.length }));
      } else if (already.length && !rejected.length) {
        showSnackbar(t('candidateGroups.alreadyMembers', 'Those candidates are already in this group.'), 'info');
      }
      if (rejected.length) {
        showSnackbar(t('candidateGroups.someRejected', 'Some candidates could not be added because they are not in this company.'), 'error');
      }
      setGroup((current) => current ? { ...current, candidate_count: response.data.candidate_count } : current);
      setPickerOpen(false);
      setSelectedIds([]);
      setCandidateSearch('');
      await loadMembers();
      await loadGroup();
    } catch (error) {
      showSnackbar(apiErrorMessage(error, t('candidateGroups.addFailed', 'Failed to add candidates.')), 'error');
    } finally {
      setAdding(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`candidate-groups/${id}/members/${removeTarget.id}/`);
      showSnackbar(t('candidateGroups.memberRemoved', 'Candidate removed from the group. Their profile was not deleted.'));
      setRemoveTarget(null);
      await loadMembers();
      await loadGroup();
    } catch (error) {
      showSnackbar(apiErrorMessage(error, t('candidateGroups.removeFailed', 'Failed to remove candidate.')), 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30" style={{ paddingTop: '10px' }}>
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
      <div className="mx-auto w-full max-w-[1920px] px-4 py-3 sm:px-6 lg:px-8">
        <button onClick={() => navigate('/candidate-groups')} className="mb-3 flex items-center gap-1 text-sm text-gray-600 hover:text-blue-600">
          <ArrowLeft size={16} />
          {t('candidateGroups.back', 'Back to Candidate Groups')}
        </button>
        <div className="overflow-hidden rounded-xl bg-white">
          <div className="rounded-t-xl px-4 py-3 text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1
                  className="mb-0.5 flex items-center gap-2.5 text-lg font-semibold text-white"
                  style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff', background: 'none', WebkitBackgroundClip: 'unset', backgroundClip: 'unset', marginBottom: '2px' }}
                >
                  <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/20 text-base leading-none" aria-hidden="true">
                    {group?.logo || '•'}
                  </span>
                  {group?.name || t('candidateGroups.title', 'Candidate Groups')}
                </h1>
                <p className="text-xs text-blue-100">
                  {group?.description || t('candidateGroups.noDescription', 'No description')}
                  {' · '}
                  {t('candidateGroups.memberCount', '{{count}} candidates', { count: group?.candidate_count ?? pagination.total_count })}
                </p>
              </div>
              <button
                onClick={() => setPickerOpen(true)}
                className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-blue-600 shadow-sm hover:bg-blue-50"
              >
                <UserPlus size={14} />
                {t('candidateGroups.addCandidates', 'Add Candidates')}
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex justify-center py-16"><CircularProgress size={40} sx={{ color: '#0ea5e9' }} /></div>
            ) : members.length === 0 ? (
              <div className="py-16 text-center text-gray-500">
                <Users size={40} className="mx-auto mb-3 opacity-50" />
                <p className="font-medium text-gray-800">{t('candidateGroups.emptyMembers', 'This group has no candidates yet.')}</p>
                <button onClick={() => setPickerOpen(true)} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">
                  {t('candidateGroups.addCandidates', 'Add Candidates')}
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50/80 text-left text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-4 py-3">{t('interviewOverview.candidate', 'Candidate')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.recentUpdate', 'Recent update')}</th>
                      <th className="px-4 py-3">{t('candidateGroups.status', 'Status')}</th>
                      <th className="px-4 py-3 text-center">{t('candidate.actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {members.map((member) => {
                      const interview = member.recent_interview;
                      const statusLabel = interview?.status === 'in_progress'
                        ? t('candidateGroups.interviewInProgress', 'In progress')
                        : interview?.status === 'completed'
                          ? t('candidateGroups.interviewCompleted', 'Completed')
                          : interview?.status === 'cancelled'
                            ? t('candidateGroups.interviewCancelled', 'Cancelled')
                            : t('candidateGroups.interviewPending', 'Pending');
                      const inviteLabel = member.inviteStatus || t('candidateGroups.noInvite', 'No invite');
                      return (
                        <tr key={member.membership_id || member.id} className="hover:bg-sky-50/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="truncate font-medium text-gray-900">{`${member.firstName || ''} ${member.lastName || ''}`.trim() || '—'}</div>
                                {member.email ? <div className="truncate text-xs text-sky-600">{member.email}</div> : null}
                              </div>
                              <Tooltip title={t('candidateGroups.viewCandidate', 'View candidate')} placement="top" enterDelay={200} slotProps={hoverTipSlotProps}>
                                <button
                                  type="button"
                                  aria-label={t('candidateGroups.viewCandidate', 'View candidate')}
                                  onClick={() => navigate(`/candidatedetails/${member.id}`)}
                                  className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sky-600 transition-colors hover:bg-sky-100"
                                >
                                  <ChevronRight size={14} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {interview ? (
                              <div>
                                <div className="font-medium text-gray-900">
                                  {interview.job_title || t('candidateGroups.interview', 'Interview')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {statusLabel}
                                  {' · '}
                                  {formatGroupDate(interview.started_at)}
                                </div>
                                {interview.status === 'completed' && (
                                  <div className="mt-1 flex items-center gap-2">
                                    <span className={`text-sm font-semibold ${scoreClass(interview.effective_score ?? interview.total_score)}`}>
                                      {formatInterviewScore(interview.effective_score ?? interview.total_score)}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/candidatedetails/${member.id}`)}
                                      className="text-xs font-medium text-sky-600 hover:text-fuchsia-600 hover:underline"
                                    >
                                      {t('candidateGroups.more', 'more')}
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
                                {t('candidateGroups.noRecentUpdate', 'No recent update')}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${inviteStatusClass[member.inviteStatus] || 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                              {inviteLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center">
                              <Tooltip title={t('candidateGroups.remove', 'Remove')} placement="top" enterDelay={200} slotProps={hoverTipSlotProps}>
                                <button
                                  type="button"
                                  aria-label={t('candidateGroups.remove', 'Remove')}
                                  onClick={() => setRemoveTarget(member)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-rose-600 transition-colors hover:bg-rose-100"
                                >
                                  <UserMinus size={15} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.total_pages}
              totalCount={pagination.total_count}
              currentCount={members.length}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl text-[10.5px]" style={{ fontSize: '10.5px' }}>
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 className="text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>{t('candidateGroups.addCandidates', 'Add Candidates')}</h2>
              <input
                className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-[10.5px]"
                style={{ fontSize: '10.5px' }}
                placeholder={t('candidateGroups.searchCandidates', 'Search candidates by name or email')}
                value={candidateSearch}
                onChange={(event) => setCandidateSearch(event.target.value)}
              />
              <p className="mt-2 text-[10.5px] text-gray-500" style={{ fontSize: '10.5px' }}>
                {t('candidateGroups.selectedCount', '{{count}} selected', { count: selectedIds.length })}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {searching ? (
                <div className="flex justify-center py-8"><CircularProgress size={24} /></div>
              ) : candidateOptions.length === 0 ? (
                <p className="py-8 text-center text-[10.5px] text-gray-500" style={{ fontSize: '10.5px' }}>{t('candidateGroups.noCandidateMatches', 'No candidates found.')}</p>
              ) : candidateOptions.map((candidate) => {
                const already = memberIds.has(candidate.id);
                const checked = selectedIds.includes(candidate.id);
                return (
                  <label key={candidate.id} className="flex items-start gap-3 border-b border-gray-100 py-2 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={already}
                      onChange={() => toggleCandidate(candidate.id)}
                    />
                    <span>
                      <span className="font-medium text-gray-900 text-[10.5px]" style={{ fontSize: '10.5px' }}>{`${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()}</span>
                      <span className="block text-[10.5px] text-gray-500" style={{ fontSize: '10.5px' }}>{candidate.email}</span>
                      {already && <span className="text-[10.5px] text-amber-700" style={{ fontSize: '10.5px' }}>{t('candidateGroups.alreadyInGroup', 'Already in this group')}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button onClick={() => { setPickerOpen(false); setSelectedIds([]); }} className="rounded-lg px-4 py-1.5 text-[10.5px] text-gray-700 hover:bg-gray-100" style={{ fontSize: '10.5px' }}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={addSelected} disabled={adding || selectedIds.length === 0} className="rounded-lg bg-blue-600 px-4 py-1.5 text-[10.5px] font-medium text-white disabled:opacity-60" style={{ fontSize: '10.5px' }}>
                {adding ? t('candidateGroups.saving', 'Saving...') : t('candidateGroups.addSelected', 'Add selected')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title={t('candidateGroups.removeTitle', 'Remove candidate from group')}
        message={t('candidateGroups.removeMessage', 'Remove this candidate from the group? The candidate, their company access, and their other groups stay unchanged.')}
        confirmText={t('candidateGroups.remove', 'Remove')}
      />
    </div>
  );
}
